import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  updateSectionItemsOrderAction,
  updateSectionAction,
} from "../../../redux/actions/careplanActions";
import { formatDate } from "../../../utils/dateUtils";
import ViewSection from "./ViewSection";
import EditSection from "./EditSection";
import SectionAuditLogs from "../AuditLog/SectionAuditLogs";
import { EyeIcon, EditIcon, QRIcon, LogIcon } from "../../common/Icons"; // Adjust the import path as necessary
import Menu from "../../common/Menu";
import Spacer from "../../common/Spacer";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import TextField from "../../common/TextField";
import QRCode from "../../common/QRCode";
import IconButton from "../../common/Button";
import { fetchSectionItems } from "../../../services/dashboardServices";

const SectionsList = ({ sections, items }) => {
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  // const [itemsForSection, setItemsForSection] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [modalButtons, setModalButtons] = useState([]); // Dynamic buttons
  const [showUpadateQR, setShowUpadateQR] = useState(false);
  const [resourceLink, setResourceLink] = useState("");
  const [description, setDescription] = useState("");
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const dispatch = useDispatch();

  const handleViewSection = (section) => {
    setSelectedSection(section);
    setIsEditMode(false); // Viewing mode
    setShowSectionModal(true);
  };

  const handleEditSection = (section) => {
    fetchSectionItems(section.id).then((fullSection) => {
      setSelectedSection(fullSection);
      setIsEditMode(true);
      setShowSectionModal(true);
      setModalButtons([
        {
          label: "Cancel",
          onClick: handleCloseSectionModal,
          variant: "secondary",
          state: "idle",
        },
      ]);
    });
  };

  const handleCloseSectionModal = () => {
    setShowSectionModal(false);
    setSelectedSection(null);
    setModalButtons([]); // Reset modal buttons
  };

  const handleSaveSection = (updatedSection) => {
    // Destructure the updatedSection data
    const { sectionId, isServiceUserSpecific, items } = updatedSection;

    // Prepare the data to send to the API
    const sectionData = {
      isServiceUserSpecific: isServiceUserSpecific,
      items_data: items, // Include the items with their order
    };

    dispatch(updateSectionItemsOrderAction({ sectionId, sectionData }));

    // Close modal after saving
    setShowSectionModal(false);
  };

  const handleShowAuditLogs = (section) => {
    setSelectedSection(section);
    setShowAuditLogs(true);
  };

  const handleCloseAuditLogs = () => {
    setShowAuditLogs(false);
    // setSelectedSection(null);
  };

  const handleShowUpdateQR = (section) => {
    setSelectedSection(section);
    setResourceLink(section.resource_link || "");
    setDescription(section.qr_description || "");
    setShowUpadateQR(true);
  };

  const handleSave = async () => {
    try {
      await dispatch(
        updateSectionAction({
          sectionId: selectedSection.id,
          resource_link: resourceLink,
          qr_description: description,
        })
      ).unwrap();
      // If successful, close the modal
      setShowUpadateQR(false);
      setShowSaveConfirmation(false);
    } catch (error) {
      // Handle error if needed
      console.error("Failed to update QR info:", error);
    }
  };

  // Define a function to generate menu items for each section
  const generateMenuItems = (section) => {
    const all = [
      {
        label: "View section",
        icon: <EyeIcon />,
        action: () => handleViewSection(section),
      },
      {
        label: "Edit section",
        icon: <EditIcon />,
        action: () => handleEditSection(section),
      },
      {
        label: "Update QR code",
        icon: <QRIcon />,
        action: () => handleShowUpdateQR(section),
      },
      {
        label: "Audit log",
        icon: <LogIcon />,
        action: () => handleShowAuditLogs(section),
      },
    ];

    // hide “View section” and “Update QR code” for section 6
    if (section.id === 6) {
      return all.filter(({ label }) => label !== "Update QR code");
    }

    return all;
  };

  return (
    <>
      {showAuditLogs && selectedSection && (
        <SectionAuditLogs
          sectionId={selectedSection.id}
          sectionTitle={selectedSection.title}
          onClose={handleCloseAuditLogs}
        />
      )}
      {showSectionModal &&
        (isEditMode ? (
          <EditSection
            section={selectedSection}
            allItems={items}
            onClose={handleCloseSectionModal}
            onSave={handleSaveSection}
            modalButtons={modalButtons}
          />
        ) : (
          <ViewSection
            section={selectedSection}
            onClose={handleCloseSectionModal}
            onEditSection={handleEditSection}
            onShowUpdateQR={handleShowUpdateQR}
            onShowAuditLogs={handleShowAuditLogs}
          />
        ))}
      {showUpadateQR && (
        <FullScreenOverlay
          title="Update QR code"
          iconClose={true}
          isSlimModal={true}
          onClose={() => setShowUpadateQR(false)}
          width="60%"
          height="80%"
          buttons={[]}
        >
          <p className="body-text-regular regular neutral-800">
            Setup the QR code that helps user to learn and get more information
            about the particular section on their Care Plan.
          </p>
          <Spacer height="32px" />
          <InputContainer>
            <label className="body-text-regular medium neutral-800">
              Enter URL <span className="error">*</span>
            </label>
            <p className="body-text-regular regular neutral-700">
              Enter the web URL to link which you want to redirect
            </p>
            <TextField
              placeholder="Enter web link here"
              maxLength={200}
              name="resource_link"
              value={resourceLink}
              onChange={(e) => setResourceLink(e.target.value)}
              required
            />
          </InputContainer>
          <Spacer />
          <InputContainer>
            <label>Description</label>
            <p className="body-text-regular regular neutral-700">
              Give a brief reason to inform the user where the QR will take them
              and why
            </p>
            <TextField
              placeholder="Enter description"
              maxLength={80}
              showCharacterLimit={true}
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </InputContainer>
          <Spacer height="16px" />
          {/* Real-time QR code preview */}
          <QRCode resourceLink={resourceLink} />
          <Spacer height="16px" />
          <ButtonContainer>
            <IconButton
              width={"100%"}
              variant="secondary"
              onClick={() => setShowUpadateQR(false)}
            >
              Cancel
            </IconButton>

            <IconButton
              width={"100%"}
              variant="primary"
              onClick={() => setShowSaveConfirmation(true)}
            >
              Save changes
            </IconButton>
          </ButtonContainer>
        </FullScreenOverlay>
      )}

      {/* Confirmation for Save */}
      {showSaveConfirmation && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="save-care-plan-confirmation"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => setShowSaveConfirmation(false),
            },
            {
              label: "Yes",
              onClick: () => handleSave(),
              variant: "primary",
            },
          ]}
        >
          <h5 className="semi-bold">Save changes?</h5>
          <p>
            By proceeding, this QR Code will be updated. You can view it in the
            print care plan view.
          </p>
        </FullScreenOverlay>
      )}

      <div className="sections-list">
        {sections.map((section, index) => (
          <SectionItem
            key={section.id}
            onClick={index !== 0 ? () => handleViewSection(section) : undefined}
            clickable={index !== 0}
          >
            <SectionTitleWrapper>
              <SectionTitle>
                <VerticalBar color={section.color} />
                {section.title}
              </SectionTitle>

              {index !== 0 && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Menu menuItems={generateMenuItems(section)} />
                </div>
              )}
            </SectionTitleWrapper>

            {/* Conditionally render the meta information for sections other than the first */}
            {index !== 0 && (
              <SectionMetaWrapper>
                <div>
                  Last updated by{" "}
                  <span className="bold neutral-700">
                    {section.updated_by_name}
                  </span>
                </div>
                <div>
                  Last updated{" "}
                  <span className="bold neutral-700">
                    {formatDate(section.last_updated_at)}
                  </span>
                </div>
              </SectionMetaWrapper>
            )}
          </SectionItem>
        ))}
        <Spacer />
      </div>
    </>
  );
};

export default SectionsList;

const SectionItem = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-100, #fff);
  margin-top: 24px;
  ${({ clickable }) =>
    clickable &&
    `
      cursor: pointer;
      &:hover {
        box-shadow: 0px 5px 15px 0px rgba(0, 0, 0, 0.1);
        transition: box-shadow 0.3s ease;
      }
    `}
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.div`
  display: flex;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  color: var(--Colors-Neutral-900, #161718);
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 120% */
`;

const VerticalBar = styled.div`
  margin-right: 16px;
  width: 4px;
  height: 24px;
  border-radius: 100px;
  align-self: stretch;
  background-color: ${({ color }) =>
    color || "var(--Colors-Status-Colors-Blue, #3A70E2)"};
`;

const SectionMetaWrapper = styled.div`
  display: flex;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 0px 0px 12px 12px;
  background: var(--Colors-Neutral-200, #f8f9fa);
  color: var(--Colors-Neutral-600, #6c757d);
  /* Body Text/Small/Regular */
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  label,
  p {
    margin-bottom: 8px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding-top: 16px;
`;
